import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@Components/common';
import styles from './DocumentsController.module.scss';
import { Documents } from '@Components/AccountPage/Documents/Documents';
import { OrganizationDocumentTypesEnum } from '@Types/OrganizationDueDiligenceData';
import { useNotificationsContext, useUserContext } from '@Contexts';
import { useGetDueDiligenceDetails, useUpdateDueDiligenceDetails } from '@Hooks/Api';
import { Loader } from '@Components';
import { cleanNumericFormatField } from '@Helpers/cleanNumericFormatField';

export const DocumentsController = ({ afterSubmit }: { afterSubmit?: () => void }) => {
  const { t } = useTranslation('account');
  const methods = useForm();
  const { reset } = methods;
  const { success: successNotification } = useNotificationsContext();

  const { userData } = useUserContext();
  const [getDueDiligenceData, { data: dueDiligenceData, loading: dueDiligenceDataLoading }] =
    useGetDueDiligenceDetails();
  const [
    updateDueDiligenceData,
    { loading: isUpdateDueDiligenceDataLoading, error: UpdateDueDiligenceDataError },
  ] = useUpdateDueDiligenceDetails();
  const organizationId = userData?.organization_id;

  useEffect(() => {
    if (organizationId) {
      getDueDiligenceData(organizationId).then(({ data }) => {
        const filteredData = {
          governing_documents: data?.key_documents
            .filter(
              (document) =>
                document.document_type === OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
            )
            .map((document) => ({ ...document, name: document.filename })),
          annual_accounts: data?.key_documents
            .filter(
              (document) =>
                document.document_type === OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
            )
            .map((document) => ({ ...document, name: document.filename })),
          annual_income: data?.annual_accounts.income,
        };
        reset(filteredData);
      });
    }
  }, [organizationId, getDueDiligenceData, reset]);

  const formatFormDataForSubmit = (data: any) => {
    const key_documents = [
      ...(Array.isArray(data.annual_accounts)
        ? data.annual_accounts.map((document: any) =>
            document.id
              ? {
                  document_type: OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
                  ...document,
                }
              : {
                  document_type: OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
                  data: document,
                },
          )
        : data.annual_accounts === null
        ? []
        : [
            {
              document_type: OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
              data: data.annual_accounts,
            },
          ]),
      ...(Array.isArray(data.governing_documents)
        ? data.governing_documents.map((document: any) =>
            document.id
              ? {
                  document_type: OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
                  ...document,
                }
              : {
                  document_type: OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
                  data: document,
                },
          )
        : data.governing_documents === null
        ? []
        : [
            {
              document_type: OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
              data: data.governing_documents || undefined,
            },
          ]),
    ];

    const annual_accounts = {
      income: cleanNumericFormatField(data.annual_income),
    };

    return { annual_accounts: annual_accounts, key_documents: key_documents };
  };

  const onSubmit = methods.handleSubmit(
    (data) => {
      const formattedData = formatFormDataForSubmit(data);

      if (organizationId) {
        updateDueDiligenceData(organizationId, formattedData).then(() => {
          successNotification(t('update_success_message', { section: t('tabs.documents.title') }));
          afterSubmit && afterSubmit();
        });
      }
    },
    () => {},
  );

  if (dueDiligenceDataLoading) {
    return <Loader />;
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className={styles.formContainer}>
          <Documents />
          <Button
            buttonType={'primary'}
            className={styles.button}
            type="submit"
            loading={isUpdateDueDiligenceDataLoading}
            disabled={isUpdateDueDiligenceDataLoading}
          >
            {t('save')}
          </Button>
        </form>
      </FormProvider>
    </>
  );
};
